import React, { useState } from "react";
import { Header } from "../../common/Header";
import { Button } from "../../ui/button";
import { Bell, Trash2 } from "lucide-react";
// import { Bell, Moon, Globe, Shield, Download, Trash2, ChevronRight } from "lucide-react";
import config from "@nusrif/config";
interface SettingItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  type: 'toggle' | 'navigation' | 'action';
  value?: boolean;
  onPress?: () => void;
  destructive?: boolean;
}
export const SettingsPage = () => {
  const [settings, setSettings] = useState({
    pushNotifications: true,
    emailNotifications: false,
    darkMode: false,
    biometricAuth: true
  });
  const handleToggleSetting = (settingKey: string) => {
    setSettings(prev => ({
      ...prev,
      [settingKey]: !prev[settingKey as keyof typeof prev]
    }));
  };
  const settingSections = [
    {
      title: "Notifications",
      items: [
        {
          id: "push-notifications",
          title: "Push Notifications",
          subtitle: "Receive notifications about orders and offers",
          icon: Bell,
          type: "toggle" as const,
          value: settings.pushNotifications,
          onPress: () => handleToggleSetting('pushNotifications')
        },
        {
          id: "email-notifications",
          title: "Email Notifications",
          subtitle: "Receive email updates about your account",
          icon: Bell,
          type: "toggle" as const,
          value: settings.emailNotifications,
          onPress: () => handleToggleSetting('emailNotifications')
        }
      ]
    },
    {
      title: "Account",
      items: [
        {
          id: "delete-account",
          title: "Delete Account",
          subtitle: "Permanently delete your account",
          icon: Trash2,
          type: "action" as const,
          destructive: true,
          onPress: () => console.log("Delete account")
        }
      ]
    }
  ];
  const ToggleSwitch = ({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) => (
    <button
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-primary-blue' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <Header 
        title="Settings" 
        showBackButton={true} 
        showCartButton={false}
      />
      
      {/* Main Content */}
      <div className="pt-20 px-5 space-y-6">
        {settingSections.map((section) => (
          <div key={section.title} className="space-y-4">
            <h2 className="text-lg font-semibold text-black">{section.title}</h2>
            
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
              {section.items.map((item, index) => {
                const Icon = item.icon;
                const settingItem = item as SettingItem;
                return (
                  <div
                    key={item.id}
                    className={`p-4 ${index < section.items.length - 1 ? 'border-b border-gray-100' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          settingItem.destructive ? 'bg-red-50' : 'bg-gray-50'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            settingItem.destructive ? 'text-red-600' : 'text-gray-600'
                          }`} />
                        </div>
                        
                        <div className="flex-1">
                          <p className={`font-medium ${
                            settingItem.destructive ? 'text-red-600' : 'text-black'
                          }`}>
                            {item.title}
                          </p>
                          {item.subtitle && (
                            <p className="text-sm text-gray-600">{item.subtitle}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Element */}
                      <div className="flex items-center">
                        {item.type === 'toggle' && (
                          <ToggleSwitch
                            enabled={item.value || false}
                            onToggle={item.onPress || (() => {})}
                          />
                        )}
                        {item.type === 'action' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`text-sm ${
                              settingItem.destructive 
                                ? 'text-red-600 hover:text-red-700 hover:bg-red-50' 
                                : 'text-primary-blue hover:text-primary-blue/80 hover:bg-blue-50'
                            }`}
                            onClick={item.onPress}
                          >
                            {settingItem.destructive ? 'Delete' : 'Action'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {/* App Version */}
        <div className="text-center py-6">
          <p className="text-sm text-gray-500">Version {config.app.version}</p>
          <p className="text-xs text-gray-400 mt-1">{`© ${new Date().getFullYear()} ${config.app.name}`}</p>
        </div>
      </div>
    </div>
  );
};